import { supabase } from '@/lib/supabase';
import { LinearGradient } from 'expo-linear-gradient';
import { Link, router } from 'expo-router';
import { useState } from 'react';
import { Alert, Image, KeyboardAvoidingView, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Surface, Text, TextInput } from 'react-native-paper';

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 500;

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  purple600: '#7C3AED',
};

export default function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  
  // ✅ État pour afficher/masquer le mot de passe
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) {
      Alert.alert('Erreur', 'Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (error) {
      setLoading(false);
      Alert.alert('Erreur de connexion', error.message);
      return;
    }

    // Vérifier si l'onboarding est complété
    const { data: profile } = await supabase
      .from('profiles')
      .select('onboarding_completed')
      .eq('id', data.user.id)
      .maybeSingle();

    setLoading(false);

    if (profile?.onboarding_completed) {
      router.replace('/(tabs)');
    } else {
      router.replace('/onboarding');
    }
  };

  // ✅ Mot de passe oublié
  const handleForgotPassword = async () => {
    if (!email || !email.trim()) {
      Alert.alert(
        'Email requis',
        'Entre ton adresse email dans le champ ci-dessus, puis clique sur "Mot de passe oublié".'
      );
      return;
    }

    setResetLoading(true);

    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: 'https://app.ernest-app.fr/reset-password',
    });

    setResetLoading(false);

    if (error) {
      Alert.alert('Erreur', error.message);
    } else {
      Alert.alert(
        'Email envoyé ! 📧',
        'Vérifie ta boîte mail (et tes spams). Clique sur le lien pour créer un nouveau mot de passe.'
      );
    }
  };

  return (
    <KeyboardAvoidingView 
      style={styles.container} 
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.content}>
        {/* Header */}
        <View style={styles.header}>
          <LinearGradient
            colors={[colors.indigo600, colors.purple600]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.logoCircle}
          >
            <Image 
              source={require('../assets/images/ernest.png')} 
              style={styles.logoImage}
            />
          </LinearGradient>
          <Text style={styles.title}>Bon retour !</Text>
          <Text style={styles.subtitle}>
            Connecte-toi pour continuer à réviser avec Ernest
          </Text>
        </View>

        {/* Form */}
        <Surface style={styles.formCard} elevation={1}>
          <View style={styles.form}>
            <TextInput
              label="Email"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
              mode="outlined"
              style={styles.input}
              outlineColor={colors.slate200}
              activeOutlineColor={colors.indigo600}
              textColor={colors.slate900}
            />

            <TextInput
              label="Mot de passe"
              value={password}
              onChangeText={setPassword}
              secureTextEntry={!showPassword}
              mode="outlined"
              style={styles.input}
              outlineColor={colors.slate200}
              activeOutlineColor={colors.indigo600}
              textColor={colors.slate900}
              right={
                <TextInput.Icon 
                  icon={showPassword ? "eye-off" : "eye"} 
                  onPress={() => setShowPassword(!showPassword)}
                  color={colors.slate400}
                />
              }
            />

            {/* ✅ Mot de passe oublié */}
            <TouchableOpacity
              onPress={handleForgotPassword}
              disabled={resetLoading}
              activeOpacity={0.7}
            >
              <Text style={styles.forgotPassword}>
                {resetLoading ? 'Envoi en cours...' : 'Mot de passe oublié ?'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.loginButton, loading && styles.loginButtonDisabled]}
              onPress={handleLogin}
              disabled={loading}
              activeOpacity={0.8}
            >
              <LinearGradient
                colors={[colors.indigo600, colors.purple600]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.loginButtonGradient}
              >
                {loading ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <Text style={styles.loginButtonText}>Se connecter</Text>
                )}
              </LinearGradient>
            </TouchableOpacity>

            <View style={styles.signupRow}>
              <Text style={styles.signupText}>
                Pas encore de compte ?{' '}
              </Text>
              <Link href="/signup" asChild>
                <TouchableOpacity>
                  <Text style={styles.signupLink}>Créer un compte</Text>
                </TouchableOpacity>
              </Link>
            </View>
          </View>
        </Surface>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  content: {
    flex: 1,
    padding: 24,
    justifyContent: 'center',
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    alignSelf: 'center',
  },
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logoCircle: {
    width: 88,
    height: 88,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 8,
  },
  logoImage: {
    width: 64,
    height: 64,
    resizeMode: 'contain',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 15,
    color: colors.slate500,
    textAlign: 'center',
    lineHeight: 22,
    paddingHorizontal: 20,
  },
  formCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 24,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 16,
    elevation: 4,
  },
  form: {
    gap: 14,
  },
  input: {
    backgroundColor: colors.white,
  },
  // ✅ Style "Mot de passe oublié"
  forgotPassword: {
    fontSize: 14,
    color: colors.indigo600,
    fontWeight: '600',
    textAlign: 'right',
  },
  loginButton: {
    marginTop: 8,
    borderRadius: 14,
    overflow: 'hidden',
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 4,
  },
  loginButtonDisabled: {
    opacity: 0.7,
  },
  loginButtonGradient: {
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginButtonText: {
    color: colors.white,
    fontSize: 17,
    fontWeight: '700',
  },
  signupRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
  },
  signupText: {
    fontSize: 14,
    color: colors.slate500,
  },
  signupLink: {
    fontSize: 14,
    color: colors.indigo600,
    fontWeight: '600',
  },
});
